import { Building2, Check, ChevronLeft, ChevronRight, CircleUserRound, KeyRound, Sparkles, UserRoundCog } from 'lucide-react'
import { useMemo, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Logo } from '../components/Logo'
import { FieldError } from '../components/UI'
import { useAuth } from '../context/AuthContext'
import { T, useI18n } from '../lib/i18n'
import {
  clearPendingInvitation,
  clearPendingTeamInvitation,
  formatInvitationCode,
  formatTeamInvitationCode,
  isValidInvitationCode,
  isValidTeamInvitationCode,
  pendingInvitation,
  pendingTeamInvitation,
} from '../lib/invitations'
import { trackMarketingEvent } from '../lib/marketing'
import { supabase } from '../lib/supabase'

type SetupKind = 'business' | 'client' | 'employee'

interface BusinessForm {
  companyName: string
  legalName: string
  ownerName: string
  email: string
  phone: string
  website: string
  einLast4: string
  address1: string
  address2: string
  city: string
  region: string
  postalCode: string
  serviceRadius: string
  defaultMinutes: string
  defaultPrice: string
  bufferMinutes: string
}

interface ClientForm {
  invitationCode: string
}

interface EmployeeForm {
  invitationCode: string
}

export function OnboardingPage() {
  const { user, refreshProfile } = useAuth()
  const { locale, t } = useI18n()
  const navigate = useNavigate()
  const initialTeamInvitation = pendingTeamInvitation()
  const initialInvitation = initialTeamInvitation ? null : pendingInvitation()
  const [step, setStep] = useState(initialInvitation || initialTeamInvitation ? 2 : 1)
  const [kind, setKind] = useState<SetupKind | null>(initialTeamInvitation ? 'employee' : initialInvitation ? 'client' : null)
  const [saving, setSaving] = useState(false)
  const [business, setBusiness] = useState<BusinessForm>({
    companyName: '', legalName: '', ownerName: String(user?.user_metadata.full_name ?? user?.user_metadata.name ?? ''),
    email: user?.email ?? '', phone: '', website: '', einLast4: '', address1: '', address2: '', city: '', region: '',
    postalCode: '', serviceRadius: '25', defaultMinutes: '120', defaultPrice: '', bufferMinutes: '30',
  })
  const [client, setClient] = useState<ClientForm>({
    invitationCode: initialInvitation ?? '',
  })
  const [employee, setEmployee] = useState<EmployeeForm>({
    invitationCode: initialTeamInvitation ?? '',
  })
  const [touched, setTouched] = useState(false)

  const businessValid = useMemo(() => Boolean(
    business.companyName.trim() && business.ownerName.trim() && /^\S+@\S+\.\S+$/.test(business.email) && business.address1.trim()
      && business.city.trim() && business.region.trim() && business.postalCode.trim()
      && (!business.einLast4 || /^\d{4}$/.test(business.einLast4)),
  ), [business])
  const clientValid = isValidInvitationCode(client.invitationCode)
  const employeeValid = isValidTeamInvitationCode(employee.invitationCode)
  const finalStep = kind === 'business' ? 3 : 2

  function next() {
    setTouched(true)
    if (step === 1 && !kind) return
    if (step === 2 && kind === 'business' && !businessValid) return
    if (step === 2 && kind === 'client' && !clientValid) return
    if (step === 2 && kind === 'employee' && !employeeValid) return
    setTouched(false)
    setStep((current) => Math.min(finalStep, current + 1))
  }

  async function complete(event: FormEvent) {
    event.preventDefault()
    if (!user || !kind) return
    if (kind === 'client' && !clientValid) {
      setTouched(true)
      return
    }
    if (kind === 'employee' && !employeeValid) {
      setTouched(true)
      return
    }
    setSaving(true)
    try {
      if (kind === 'business') {
        if (!businessValid) throw new Error(t('Complete the required company fields.'))
        const { error: companyError } = await supabase.from('organizations').insert({
          id: crypto.randomUUID(),
          created_by: user.id,
          display_name: business.companyName.trim(),
          legal_name: business.legalName.trim() || null,
          business_type: 'Residential cleaning',
          email: business.email.trim().toLowerCase(),
          phone_e164: business.phone.trim() || null,
          website: business.website.trim() || null,
          ein_last4: business.einLast4 || null,
          address_line1: business.address1.trim(),
          address_line2: business.address2.trim() || null,
          city: business.city.trim(),
          region: business.region.trim(),
          postal_code: business.postalCode.trim(),
          country_code: 'US',
          time_zone_identifier: Intl.DateTimeFormat().resolvedOptions().timeZone,
          service_radius_miles: Number(business.serviceRadius) || 25,
          default_service_minutes: Number(business.defaultMinutes) || 120,
          default_price_cents: Math.round((Number(business.defaultPrice) || 0) * 100),
          schedule_buffer_minutes: Number(business.bufferMinutes) || 30,
        })
        if (companyError) throw companyError
        const { error: profileError } = await supabase.from('profiles').update({
          display_name: business.ownerName.trim(),
          phone_e164: business.phone.trim() || null,
          preferred_locale: locale,
          default_account_kind: 'business',
          onboarding_completed_at: new Date().toISOString(),
        }).eq('id', user.id)
        if (profileError) throw profileError
      } else if (kind === 'client') {
        const code = formatInvitationCode(client.invitationCode)
        if (!isValidInvitationCode(code)) throw new Error(t('Enter the complete invitation code from your cleaning company.'))
        const { error: invitationError } = await supabase.rpc('redeem_client_invitation', { invitation_code: code })
        if (invitationError) throw invitationError
        const identityName = String(user.user_metadata.full_name ?? user.user_metadata.name ?? '').trim()
        const { error: profileError } = await supabase.from('profiles').update({
          display_name: identityName || user.email?.split('@')[0] || null,
          preferred_locale: locale,
          default_account_kind: 'client',
          onboarding_completed_at: new Date().toISOString(),
        }).eq('id', user.id)
        if (profileError) throw profileError
        clearPendingInvitation()
      } else {
        const code = formatTeamInvitationCode(employee.invitationCode)
        if (!isValidTeamInvitationCode(code)) throw new Error(t('Enter the complete TEAM invitation code.'))
        const { error: invitationError } = await supabase.rpc('redeem_employee_invitation', { invitation_code: code })
        if (invitationError) throw invitationError
        clearPendingTeamInvitation()
      }
      if (kind === 'business') void trackMarketingEvent('signup_completed', locale, { source: 'web' })
      await refreshProfile()
      toast.success(t('Your TidyPing workspace is ready.'))
      navigate('/', { replace: true })
    } catch (error) {
      toast.error(error instanceof Error ? t(error.message) : t('Registration could not be completed.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <main className="onboarding-page">
      <header className="onboarding-topbar"><Logo /><span>{t('Step {current} of {total}', { current: step, total: finalStep })}</span></header>
      <div className="onboarding-progress"><span style={{ width: `${(step / finalStep) * 100}%` }} /></div>
      <form className="onboarding-card" onSubmit={complete}>
        {step === 1 && (
          <section className="onboarding-step">
            <span className="eyebrow"><T>{"Let’s personalize TidyPing"}</T></span>
            <h1><T>{"How will you use the app?"}</T></h1>
            <p><T>{"Your account type controls the tools and information you see."}</T></p>
            <div className="role-choice-grid">
              <button type="button" className={kind === 'business' ? 'role-choice role-choice--selected' : 'role-choice'} onClick={() => setKind('business')}>
                <span className="role-choice__icon"><Building2 /></span>
                <span><strong><T>{"Cleaning company"}</T></strong><small><T>{"Manage clients, schedule, team, messages, and invoices."}</T></small></span>
                <span className="role-choice__check">{kind === 'business' && <Check size={16} />}</span>
              </button>
              <button type="button" className={kind === 'client' ? 'role-choice role-choice--selected' : 'role-choice'} onClick={() => setKind('client')}>
                <span className="role-choice__icon"><CircleUserRound /></span>
                <span><strong><T>{"Cleaning client"}</T></strong><small><T>{"Confirm visits, request changes, chat, and view invoices."}</T></small></span>
                <span className="role-choice__check">{kind === 'client' && <Check size={16} />}</span>
              </button>
              <button type="button" className={kind === 'employee' ? 'role-choice role-choice--selected' : 'role-choice'} onClick={() => setKind('employee')}>
                <span className="role-choice__icon"><UserRoundCog /></span>
                <span><strong><T>{"Cleaning team member"}</T></strong><small><T>{"Join with the private code issued for your verified work email."}</T></small></span>
                <span className="role-choice__check">{kind === 'employee' && <Check size={16} />}</span>
              </button>
            </div>
            {touched && !kind && <FieldError><T>{"Choose one account type to continue."}</T></FieldError>}
          </section>
        )}

        {step === 2 && kind === 'business' && (
          <section className="onboarding-step">
            <span className="eyebrow"><T>{"Company profile"}</T></span><h1><T>{"Tell us about your business"}</T></h1>
            <p><T>{"This information appears on invoices and helps TidyPing build a practical schedule."}</T></p>
            <div className="form-grid form-grid--two">
              <label><span><T>{"Company name *"}</T></span><input value={business.companyName} onChange={(e) => setBusiness({ ...business, companyName: e.target.value })} autoFocus /></label>
              <label><span><T>{"Legal name"}</T></span><input value={business.legalName} onChange={(e) => setBusiness({ ...business, legalName: e.target.value })} /></label>
              <label><span><T>{"Owner / responsible person *"}</T></span><input value={business.ownerName} onChange={(e) => setBusiness({ ...business, ownerName: e.target.value })} /></label>
              <label><span><T>{"Business email *"}</T></span><input type="email" value={business.email} onChange={(e) => setBusiness({ ...business, email: e.target.value })} /></label>
              <label><span><T>{"Phone"}</T></span><input type="tel" value={business.phone} onChange={(e) => setBusiness({ ...business, phone: e.target.value })} /></label>
              <label><span><T>{"Website"}</T></span><input type="url" placeholder="https://" value={business.website} onChange={(e) => setBusiness({ ...business, website: e.target.value })} /></label>
              <label className="span-two"><span><T>{"Street address *"}</T></span><input value={business.address1} onChange={(e) => setBusiness({ ...business, address1: e.target.value })} /></label>
              <label><span><T>{"Suite / unit"}</T></span><input value={business.address2} onChange={(e) => setBusiness({ ...business, address2: e.target.value })} /></label>
              <label><span><T>{"City *"}</T></span><input value={business.city} onChange={(e) => setBusiness({ ...business, city: e.target.value })} /></label>
              <label><span><T>{"State *"}</T></span><input value={business.region} onChange={(e) => setBusiness({ ...business, region: e.target.value })} /></label>
              <label><span><T>{"ZIP code *"}</T></span><input inputMode="numeric" value={business.postalCode} onChange={(e) => setBusiness({ ...business, postalCode: e.target.value })} /></label>
              <label><span><T>{"EIN — last 4 digits"}</T></span><input inputMode="numeric" maxLength={4} value={business.einLast4} onChange={(e) => setBusiness({ ...business, einLast4: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></label>
            </div>
            {touched && !businessValid && <FieldError><T>{"Complete the required fields and check the email/EIN format."}</T></FieldError>}
          </section>
        )}

        {step === 2 && kind === 'client' && (
          <section className="onboarding-step onboarding-step--narrow">
            <span className="eyebrow"><T>{"Company invitation"}</T></span><h1><T>{"Connect your cleaning company"}</T></h1>
            <p><T>{"Paste the private code the company sent you. Your name, service address, cleanings, chat, and invoices come from the client record they already created."}</T></p>
            <label className="invitation-field"><span><T>{"Invitation code *"}</T></span><div><KeyRound size={20} /><input autoFocus placeholder="TIDY-XXXX-XXXX-XXXX-XXXX" value={client.invitationCode} onChange={(e) => setClient({ invitationCode: formatInvitationCode(e.target.value) })} /></div><small><T>{"You can also open the private invitation link the company shared."}</T></small></label>
            {touched && !clientValid && <FieldError><T>{"Enter the complete invitation code from your company."}</T></FieldError>}
            <div className="secure-link-note"><Check size={18} /><span><T>{"No duplicate profile: this code links only the exact client record selected by the company."}</T></span></div>
          </section>
        )}

        {step === 2 && kind === 'employee' && (
          <section className="onboarding-step onboarding-step--narrow">
            <span className="eyebrow"><T>{"Private team invitation"}</T></span><h1><T>{"Join your cleaning company"}</T></h1>
            <p><T>{"Use the one-time code from the owner or manager. It only works with the exact verified work email and role selected by the company."}</T></p>
            <label className="invitation-field"><span><T>{"Team invitation code *"}</T></span><div><KeyRound size={20} /><input autoFocus placeholder="TEAM-XXXX-XXXX-XXXX-XXXX" value={employee.invitationCode} onChange={(e) => setEmployee({ invitationCode: formatTeamInvitationCode(e.target.value) })} /></div><small><T>{"You can also open the private team invitation link."}</T></small></label>
            {touched && !employeeValid && <FieldError><T>{"Enter the complete TEAM invitation code."}</T></FieldError>}
            <div className="secure-link-note"><Check size={18} /><span><T>{"The invitation is single-use, expires automatically, and cannot be used to change your company role."}</T></span></div>
          </section>
        )}

        {step === 3 && kind === 'business' && (
          <section className="onboarding-step">
            <span className="eyebrow"><T>{"Operations"}</T></span><h1><T>{"Set your scheduling defaults"}</T></h1>
            <p><T>{"You can change these later. They simply make every new cleaning faster to create."}</T></p>
            <div className="form-grid form-grid--two">
              <label><span><T>{"Typical cleaning length"}</T></span><select value={business.defaultMinutes} onChange={(e) => setBusiness({ ...business, defaultMinutes: e.target.value })}><option value="60"><T>{"1 hour"}</T></option><option value="90"><T>{"1.5 hours"}</T></option><option value="120"><T>{"2 hours"}</T></option><option value="180"><T>{"3 hours"}</T></option><option value="240"><T>{"4 hours"}</T></option></select></label>
              <label><span><T>{"Default cleaning price"}</T></span><div className="input-prefix"><span>$</span><input type="number" min="0" step="0.01" value={business.defaultPrice} onChange={(e) => setBusiness({ ...business, defaultPrice: e.target.value })} /></div></label>
              <label><span><T>{"Travel buffer"}</T></span><select value={business.bufferMinutes} onChange={(e) => setBusiness({ ...business, bufferMinutes: e.target.value })}><option value="0"><T>{"No buffer"}</T></option><option value="15"><T>{"15 minutes"}</T></option><option value="30"><T>{"30 minutes"}</T></option><option value="45"><T>{"45 minutes"}</T></option><option value="60"><T>{"1 hour"}</T></option></select></label>
              <label><span><T>{"Service radius"}</T></span><div className="input-suffix"><input type="number" min="1" max="250" value={business.serviceRadius} onChange={(e) => setBusiness({ ...business, serviceRadius: e.target.value })} /><span><T>{"miles"}</T></span></div></label>
            </div>
            <div className="trial-note"><Sparkles size={20} /><div><strong><T>{"Your 30-day complimentary access starts automatically"}</T></strong><span><T>{"No payment information, no charge, and no automatic renewal. Clients always use TidyPing free."}</T></span></div></div>
          </section>
        )}

        <footer className="onboarding-actions">
          <button type="button" className="button button--ghost" disabled={step === 1 || saving} onClick={() => setStep((current) => current - 1)}><ChevronLeft size={17} /><T>{"Back"}</T></button>
          {step < finalStep ? (
            <button type="button" className="button button--primary" onClick={next}><T>{"Continue"}</T><ChevronRight size={17} /></button>
          ) : (
            <button type="submit" className="button button--primary" disabled={saving}>{t(saving ? 'Connecting…' : kind === 'business' ? 'Create company workspace' : kind === 'employee' ? 'Join company team' : 'Connect my account')}<Check size={17} /></button>
          )}
        </footer>
      </form>
    </main>
  )
}
